import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  BuildingOfficeIcon,
  MapPinIcon,
  PhotoIcon,
  ArrowLeftIcon,
  
  VideoCameraIcon,
  TagIcon,
  PlusIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { API_BASE } from '../lib/config';

const API = API_BASE;

const EmployerProfileForm = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const DEFAULT_CATEGORIES = [
    { id: 'cat-food', name: '飲食・フード', icon: '🍽️' },
    { id: 'cat-retail', name: '販売・接客', icon: '🛍️' },
    { id: 'cat-office', name: 'オフィス・事務', icon: '💼' },
    { id: 'cat-event', name: 'イベント・エンタメ', icon: '🎪' },
    { id: 'cat-logi', name: '倉庫・物流', icon: '📦' },
    { id: 'cat-care', name: '介護・福祉', icon: '🩺' },
  ];
  const [existingProfile, setExistingProfile] = useState(null);
  const [formData, setFormData] = useState({
    business_name: '',
    description: '',
    categories: [], // 複数選択対応
    location: '',
    prefecture: '',
    city: '',
    address: '',
    latitude: null,
    longitude: null,
    images: [],
    videos: [],
    thumbnail_image: null,
    // 新しい表示用フィールド
    title: '',
    work_hours: '',
    hourly_rate: '',
    work_location: '',
    message_to_jobseekers: '', // 求職者へのメッセージ（任意）
    tags: [],
    recruitment_dates: [],
    daily_capacity: {}
  });
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    if (!user || user.user_type !== 'employer') {
      toast.error('事業者権限が必要です');
      navigate('/');
      return;
    }
    
    fetchCategories();
    fetchExistingProfile();
  }, [user, navigate]);

  // カテゴリ一覧取得
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API}/categories`);
      if (response.ok) {
        const data = await response.json();
        const normalized = Array.isArray(data)
          ? data.map((c) => ({ id: c.id || c.name, name: c.name, icon: c.icon }))
          : [];
        setCategories(normalized.length > 0 ? normalized : DEFAULT_CATEGORIES);
      } else {
        setCategories(DEFAULT_CATEGORIES);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories(DEFAULT_CATEGORIES);
    }
  };

  // 既存のプロフィール取得（互換エンドポイント）
  const fetchExistingProfile = async () => {
    try {
      const response = await fetch(`${API}/employers/profile`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) return;
      const myProfile = await response.json();
      if (!myProfile) return;
      setExistingProfile(myProfile);
      setFormData({
        business_name: myProfile.business_name || '',
        description: myProfile.description || '',
        categories: myProfile.categories || [],
        location: myProfile.location || '',
        prefecture: myProfile.prefecture || '',
        city: myProfile.city || '',
        address: myProfile.address || '',
        latitude: myProfile.latitude ?? null,
        longitude: myProfile.longitude ?? null,
        images: myProfile.images || [],
        videos: myProfile.videos || [],
        thumbnail_image: myProfile.thumbnail_image || null,
        title: myProfile.title || '',
        work_hours: myProfile.work_hours || '',
        hourly_rate: myProfile.hourly_rate || '',
        work_location: myProfile.work_location || '',
        message_to_jobseekers: myProfile.message_to_jobseekers || '',
        tags: myProfile.tags || [],
        recruitment_dates: myProfile.recruitment_dates || [],
        daily_capacity: myProfile.daily_capacity || {}
      });
    } catch (error) {
      console.error('Error fetching existing profile:', error);
    }
  };

  // カテゴリ選択処理
  const handleCategoryChange = (categoryName) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(categoryName)
        ? prev.categories.filter(c => c !== categoryName)
        : [...prev.categories, categoryName]
    }));
  };

  // タグ追加
  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  // タグ削除
  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // 画像アップロード処理
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (formData.images.length + files.length > 5) {
      toast.error('画像は最大5枚まで登録できます');
      return;
    }

    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target.result.split(',')[1];
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, base64]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  // 動画アップロード処理
  const handleVideoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (formData.videos.length >= 1) {
      toast.error('動画は1本まで登録できます');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result.split(',')[1];
      setFormData(prev => ({
        ...prev,
        videos: [base64]
      }));
    };
    reader.readAsDataURL(file);
  };

  // 画像削除
  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
      thumbnail_image: prev.thumbnail_image === prev.images[index] ? null : prev.thumbnail_image
    }));
  };

  // 動画削除
  const removeVideo = () => {
    setFormData(prev => ({
      ...prev,
      videos: []
    }));
  };

  // サムネイル設定
  const setThumbnail = (image) => {
    setFormData(prev => ({
      ...prev,
      thumbnail_image: image
    }));
  };

  // フォーム送信
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (false && formData.categories.length === 0) {
      toast.error('カテゴリを1つ以上選択してください');
      return;
    }

    try {
      setLoading(true);
      
      // 互換エンドポイント（PUTで作成/更新を吸収）
      const url = `${API}/employers/profile`;
      const method = 'PUT';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(existingProfile ? 'プロフィールを更新しました' : 'プロフィールを作成しました');
        navigate('/employer-dashboard');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'プロフィールの保存に失敗しました');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('ネットワークエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  const [customCategory, setCustomCategory] = useState('');

  const addCustomCategory = () => {
    const name = (customCategory || '').trim();
    if (!name) return;
    // 既存に無ければ候補へも追加
    if (!(categories || []).some((c) => c.name === name)) {
      setCategories((prev) => [...prev, { id: `custom-${Date.now()}`, name, icon: '🔖' }]);
    }
    // 選択状態に反映
    setFormData((prev) => ({
      ...prev,
      categories: prev.categories.includes(name) ? prev.categories : [...prev.categories, name],
    }));
    setCustomCategory('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <button
            onClick={() => navigate('/employer-dashboard')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            事業者ダッシュボードに戻る
          </button>
          <h1 className="text-3xl font-bold text-gray-900">
            {existingProfile ? 'プロフィール編集' : 'プロフィール作成'}
          </h1>
          <p className="text-gray-600 mt-2">
            事業者プロフィールを{existingProfile ? '編集' : '作成'}して、求職者にアピールしましょう
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* 基本情報 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <BuildingOfficeIcon className="h-6 w-6 mr-2" />
              基本情報
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  事業所名 *
                </label>
                <input
                  type="text"
                  value={formData.business_name}
                  onChange={(e) => setFormData(prev => ({...prev, business_name: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="例: カフェドリーム渋谷店"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  事業内容・紹介文 *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={4}
                  placeholder="事業内容や職場の雰囲気を求職者にアピールしてください"
                  required
                />
              </div>
            </div>
          </div>

          {/* 表示情報 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <TagIcon className="h-6 w-6 mr-2" />
              表示情報
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  タイトル（最大50文字）
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="例: アットホームなカフェでスタッフ募集中！"
                  maxLength={50}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.title.length}/50文字（トップページでは45文字まで表示）
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  勤務時間帯
                </label>
                <input
                  type="text"
                  value={formData.work_hours}
                  onChange={(e) => setFormData(prev => ({...prev, work_hours: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="例: 9:00-17:00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  時給（円）
                </label>
                <input
                  type="number"
                  value={formData.hourly_rate}
                  onChange={(e) => setFormData(prev => ({...prev, hourly_rate: e.target.value ? parseFloat(e.target.value) : ''}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="例: 1200"
                  min="0"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  勤務場所
                </label>
                <input
                  type="text"
                  value={formData.work_location}
                  onChange={(e) => setFormData(prev => ({...prev, work_location: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="例: 東京都渋谷区道玄坂1-1-1"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  求職者へのメッセージ（任意）
                </label>
                <textarea
                  value={formData.message_to_jobseekers}
                  onChange={(e) => setFormData(prev => ({...prev, message_to_jobseekers: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="求職者に向けたメッセージをご記入ください（例：アットホームな職場で一緒に働きませんか？）"
                  maxLength={300}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.message_to_jobseekers.length}/300文字
                </p>
              </div>
            </div>
          </div>

          

          {/* 所在地情報 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <MapPinIcon className="h-6 w-6 mr-2" />
              所在地情報
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  都道府県 *
                </label>
                <input
                  type="text"
                  value={formData.prefecture}
                  onChange={(e) => setFormData(prev => ({...prev, prefecture: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="東京都"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  市区町村 *
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData(prev => ({...prev, city: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="渋谷区"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  番地・建物名 *
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({...prev, address: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="渋谷1-1-1 〇〇ビル3F"
                  required
                />
              </div>
            </div>
          </div>

          {/* 画像・動画 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <PhotoIcon className="h-6 w-6 mr-2" />
              画像・動画
            </h2>

            {/* 画像アップロード */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                画像（最大5枚）
              </label>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              
              {formData.images.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">アップロード済み画像:</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {formData.images.map((image, index) => (
                      <div key={index} className="relative">
                        <img
                          src={`data:image/jpeg;base64,${image}`}
                          alt={`Upload ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setThumbnail(image)}
                          className={`absolute bottom-1 left-1 px-2 py-1 text-xs rounded ${
                            formData.thumbnail_image === image
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-800 text-white hover:bg-gray-700'
                          }`}
                        >
                          {formData.thumbnail_image === image ? 'メイン' : 'メインに設定'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* 動画アップロード */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                動画（1本まで）
              </label>
              <input
                type="file"
                accept="video/*"
                onChange={handleVideoUpload}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              
              {formData.videos.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">アップロード済み動画:</p>
                  <div className="relative inline-block">
                    <video
                      src={`data:video/mp4;base64,${formData.videos[0]}`}
                      className="w-48 h-32 object-cover rounded-lg"
                      controls
                    />
                    <button
                      type="button"
                      onClick={removeVideo}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* タグ */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              タグ（特徴）
            </h2>
            
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="例: 未経験歓迎、高校生OK、駅近"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
              />
              <button
                type="button"
                onClick={addTag}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                追加
              </button>
            </div>
            
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="ml-2 text-gray-500 hover:text-gray-700"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* 送信ボタン */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-500 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  保存中...
                </>
              ) : (
                existingProfile ? 'プロフィールを更新' : 'プロフィールを作成'
              )}
            </button>
            
            <button
              type="button"
              onClick={() => navigate('/employer-dashboard')}
              className="bg-gray-500 text-white px-8 py-3 rounded-lg font-medium hover:bg-gray-600 transition-colors flex items-center"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              ダッシュボードに戻る
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployerProfileForm;